import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Phone, Star, TrendingUp } from "lucide-react";

const PACKAGES = [
  {
    name: "Starter",
    price: 197,
    tagline: "Get visible, get reviews, catch every call.",
    features: [
      { icon: Sparkles, text: "AI Visibility monitoring · weekly re-audit + report" },
      { icon: Star, text: "Review Generation Machine · auto-text customers for 5★ reviews" },
      { icon: Phone, text: "Missed-Call Text-Back · never lose a lead to voicemail again" },
    ],
    cta: "Start with Starter",
    tone: { bg: "#FFFFFF", border: "#E5D9CB", accent: "#0A192F" },
  },
  {
    name: "Growth",
    price: 497,
    badge: "Most Popular",
    tagline: "Everything Starter has + reactivation + AI receptionist.",
    features: [
      { icon: Check, text: "Everything in Starter" },
      { icon: TrendingUp, text: "Database Reactivation · monthly campaigns to dormant customers" },
      { icon: Phone, text: "AI Answering Service · 24/7 voice AI books appointments" },
      { icon: Sparkles, text: "Priority email support · 1 business day response" },
    ],
    cta: "Book a Growth demo",
    tone: { bg: "#0A192F", border: "#0A192F", accent: "#E24E1B", text: "#FFFFFF" },
  },
  {
    name: "Domination",
    price: 997,
    tagline: "Full-stack local dominance. Nothing else touches you in your city.",
    features: [
      { icon: Check, text: "Everything in Growth" },
      { icon: Sparkles, text: "Weekly GBP posts + photo refresh (managed)" },
      { icon: Star, text: "Monthly re-audit · before → after ROI report emailed to you" },
      { icon: Phone, text: "Direct-line strategist · Slack/Signal access to your account lead" },
    ],
    cta: "Talk to us about Domination",
    tone: { bg: "#FFF6EE", border: "#E24E1B", accent: "#E24E1B" },
  },
];

const money = (n) => `$${n.toLocaleString()}`;

export default function PricingPackages() {
  return (
    <div className="min-h-screen bg-[#FFF6EE] text-[#0A192F]" data-testid="pricing-packages-page">
      <div className="border-b border-[#E5D9CB] bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight no-underline text-[#0A192F]">
            Clients<span className="text-[#E24E1B]">OnRamp</span>
          </Link>
          <Link to="/book" className="text-sm text-neutral-600 hover:text-black no-underline">Book a call →</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white border border-[#E5D9CB] px-3 py-1.5 text-xs font-mono uppercase tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#E24E1B]" /> Growth packages
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mb-4">Pick your growth speed.</h1>
          <p className="text-neutral-600 max-w-2xl mx-auto text-lg">
            One flat monthly fee. No hidden setup, no per-lead surcharges, no annual contracts. Cancel anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PACKAGES.map((p) => (
            <div
              key={p.name}
              data-testid={`pkg-${p.name.toLowerCase()}`}
              className="p-8 border-2 relative flex flex-col"
              style={{ background: p.tone.bg, borderColor: p.tone.border, color: p.tone.text || "#0A192F" }}
            >
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E24E1B] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">
                  {p.badge}
                </div>
              )}
              <div className="font-display text-2xl font-black mb-1">{p.name}</div>
              <div className="text-sm opacity-70 mb-6">{p.tagline}</div>
              <div className="mb-6">
                <span className="font-display text-5xl font-black" style={{ color: p.tone.accent }}>{money(p.price)}</span>
                <span className="text-sm opacity-70 ml-1">/mo</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <f.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.tone.accent }} />
                    <span>{f.text}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/book"
                data-testid={`pkg-${p.name.toLowerCase()}-cta`}
                className="block text-center py-3 font-semibold no-underline transition"
                style={{
                  background: p.tone.accent,
                  color: p.name === "Growth" ? "#FFFFFF" : (p.tone.text ? "#0A192F" : "#FFFFFF"),
                }}
              >
                {p.cta} <ArrowRight className="w-4 h-4 inline ml-1" />
              </Link>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E5D9CB] p-8 text-center">
          <div className="font-display text-2xl font-bold mb-2">Not sure which fits?</div>
          <p className="text-neutral-600 mb-4 max-w-xl mx-auto">Book a free 20-min strategy call. We'll run your AI Visibility audit live on the call and recommend the package that pays for itself fastest.</p>
          <Link to="/book" className="inline-flex items-center gap-2 bg-[#E24E1B] text-white px-6 py-3 font-semibold no-underline hover:bg-[#0A192F] transition">
            Book my strategy call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
